import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const QUESTIONS = [
  ["experience", "How did you find your Glimpse?", ["It made me think", "It was interesting", "I'm not sure yet", "It wasn't helpful"]],
  ["clarity", "Were the items easy to understand?", ["Yes", "Somewhat", "No"]],
  ["difficulty", "Were there any statements you found difficult to answer?", ["No, they were all clear.", "Yes, one or two.", "Yes, several."]],
];
const box = "w-full bg-[#0D0E12] border border-[var(--cg-border)] rounded-lg px-4 py-3 text-sm text-[#F3F4F6] focus:border-brass focus:outline-none transition-colors";

const Choice = ({ name, q, options, value, onChange }) => (
  <fieldset className="mt-6" data-testid={`feedback-q-${name}`}>
    <legend className="text-[#F3F4F6] font-medium mb-3">{q}</legend>
    <div className="flex flex-wrap gap-2">
      {options.map((o, i) => (
        <button type="button" key={o} onClick={() => onChange(value === o ? null : o)} aria-pressed={value === o}
          data-testid={`feedback-${name}-${i}`}
          className={`px-4 py-2 rounded-full text-sm border transition-colors ${value === o ? "border-brass bg-[rgba(217,162,95,0.12)] text-[#F3F4F6]" : "border-[var(--cg-border)] text-[#9CA3AF] hover:border-brass/60"}`}>{o}</button>
      ))}
    </div>
  </fieldset>
);

export const GlimpseFeedback = ({ statements, submitted }) => {
  const [a, setA] = useState({ experience: null, clarity: null, difficulty: null });
  const [items, setItems] = useState([]);
  const [why, setWhy] = useState("");
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(submitted);
  const yes = a.difficulty?.startsWith("Yes");
  const any = a.experience || a.clarity || a.difficulty || comments.trim();
  const toggle = (id) => setItems((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    if (busy || done) return;
    setBusy(true);
    try {
      await api.post("/glimpse/feedback", { ...a, difficult_item_ids: yes ? items : [], difficulty_explanation: yes ? why : null, comments });
      setDone(true);
    } catch (e) {
      if (e.response?.status === 409) setDone(true);
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="cg-card p-7 sm:p-8 flex items-start gap-3" data-testid="glimpse-feedback-thanks">
      <Check className="w-5 h-5 text-brass mt-1" />
      <div><h2 className="font-serif text-2xl text-[#F3F4F6]">Thank you for your feedback.</h2>
        <p className="text-sm text-[#9CA3AF] mt-1">It helps us keep improving the Authority Glimpse.</p></div>
    </div>
  );
  return (
    <div className="cg-card p-7 sm:p-8" data-testid="glimpse-feedback">
      <p className="cg-eyebrow mb-2">Optional</p>
      <h2 className="font-serif text-3xl text-[#F3F4F6]">How Was Your Authority Glimpse?</h2>
      {QUESTIONS.map(([k, q, o]) => <Choice key={k} name={k} q={q} options={o} value={a[k]} onChange={(v) => setA({ ...a, [k]: v })} />)}
      {yes && (
        <div className="mt-6 border-l-2 border-brass/40 pl-5" data-testid="feedback-difficult-section">
          <p className="text-[#F3F4F6] font-medium">Which statements were difficult, and what made them difficult to answer?</p>
          <p className="text-xs text-[#6B7280] mt-1 mb-3">Optional — tick any that apply.</p>
          <div className="space-y-2 sm:max-h-80 sm:overflow-y-auto pr-1">
            {statements.map((s) => (
              <label key={s.id} className="flex items-start gap-3 text-sm text-[#B9BDC7] cursor-pointer" data-testid={`feedback-item-${s.id}`}>
                <input type="checkbox" checked={items.includes(s.id)} onChange={() => toggle(s.id)} className="mt-1 accent-[#D9A25F]" />
                <span>{s.text}</span>
              </label>
            ))}
          </div>
          <textarea value={why} onChange={(e) => setWhy(e.target.value)} maxLength={2000} rows={3} placeholder="What made them difficult? (optional)"
            className={`${box} mt-4`} data-testid="feedback-difficulty-explanation" />
        </div>
      )}
      <label className="block mt-6 text-[#F3F4F6] font-medium" htmlFor="fb-comments">Anything else you'd like us to know?</label>
      <textarea id="fb-comments" value={comments} onChange={(e) => setComments(e.target.value)} maxLength={2000} rows={3}
        className={`${box} mt-3`} data-testid="feedback-comments" />
      <button onClick={submit} disabled={busy || !any} data-testid="feedback-submit"
        className="mt-6 bg-brass text-obsidian font-medium px-6 py-3 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-50">
        {busy ? "Sending…" : "Submit Feedback"}
      </button>
    </div>
  );
};
